import { useState, Fragment, FormEventHandler, SyntheticEvent } from "react";
import { Transition, Listbox } from "@headlessui/react";
import { Link, useNavigate } from "react-router-dom";
import clsx from "clsx";
import { REACT_APP_MANAGER_LINK } from "../../utils";
import Chevron from "../../learner/components/Chevron";
import { useDocumentTitle } from "usehooks-ts";
import { DataTable } from "./DataTable/DataTable";
import {
  DataTableContextProvider,
  useDataTable,
} from "./DataTable/DataTableContext";
import { TableHeaderSelectFilter } from "./DataTable/TableHeaderSelectFilter";
import { IInvoice } from "../utils/quoteUtils";
import { InvoiceStatusDot } from "./Invoice/InvoiceStatusDot";
import { EmptyCell } from "./DataTable/EmptyCell";

const QUOTE_OPTIONS = [
  {
    value: "quoteNumber",
    label: "Quote Number",
    placeholder: "Quote #",
  },
  {
    value: "name",
    label: "School / District Name",
    placeholder: "Name",
  },
  {
    value: "emailDomain",
    label: "Contact Email",
    placeholder: "Email",
  },
  { value: "id", label: "NCES ID", placeholder: "7654321" },
];

const TRANSITION_PARAMS = {
  enter: "transition ease-out duration-200",
  enterFrom: "opacity-0 translate-y-1",
  enterTo: "opacity-100 translate-y-0",
  leave: "transition ease-in duration-150",
  leaveFrom: "opacity-100 translate-y-0",
  leaveTo: "opacity-0 translate-y-1",
};

function QuoteFinder() {
  useDocumentTitle("Quote Finder");

  return (
    <DataTableContextProvider
      source="/manager_new/invoices/all_invoices/list"
      defaults={{
        sortBy: [{ id: "quoteNumber", desc: true }],
      }}
      downloadButton={false}
      columnOverrides={{
        expiration: ({ value }) => {
          if (value) {
            const date = new Date(value * 1000);
            return date.toLocaleDateString();
          }
          return <EmptyCell />;
        },
        quoteNumber: ({ value }) => (
          <Link
            to={`${REACT_APP_MANAGER_LINK}/quote-lookup/${value}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-dm-brand-blue-500 hover:underline"
          >
            {value}
          </Link>
        ),
        status: ({ row }) => (
          <div className="flex items-center gap-x-2">
            <InvoiceStatusDot
              status={(row.original as any)?.status}
              expiration={(row.original as any)?.expiration}
            />
            {(row.original as any)?.status || "None"}
          </div>
        ),
      }}
      filterable={[
        "name",
        "amount",
        "id",
        "contactEmail",
        "adminEmail",
        "type",
        "licenseTier",
        "status",
      ]}
      sortable={["quoteNumber", "expiration", "name", "amount", "id", "type"]}
      filterOverrides={{
        licenseTier: ({ column }) => (
          <TableHeaderSelectFilter
            key={column.id}
            className="!min-w-32"
            choices={["Plus", "Integral", "Not Set"]}
            column={column}
          />
        ),
        status: ({ column }) => (
          <TableHeaderSelectFilter
            key={column.id}
            className="!min-w-32"
            multi
            choices={[
              { label: "Active", value: "active" },
              { label: "Pending", value: "pending" },
              { label: "Expired", value: "expired" },
              { label: "None", value: "none" },
            ]}
            column={column}
          />
        ),
      }}
      getRowProps={(row) => {
        const expiration = row.original.expiration as number | undefined;
        const status = row.original.status as IInvoice["status"];
        const expired =
          expiration && expiration < Math.floor(Date.now() / 1000);
        if (expired && status !== "Activated") {
          // stale quote
          return {
            className: "italic",
          };
        } else if (status === "Activated") {
          // activated quote
          return {
            className: "font-bold",
          };
        }
        return {};
      }}
    >
      <QuoteFinderContent />
    </DataTableContextProvider>
  );
}

const QuoteFinderContent: React.FC = () => {
  const { setExternalFilterBy, columns } = useDataTable();
  const [quoteSearch, setQuoteSearch] = useState("");
  const [activeQuoteOption, setActiveQuoteOption] = useState(QUOTE_OPTIONS[0]);
  const [showReceivedOrderOnly, setShowReceivedOrderOnly] = useState(false);
  const navigate = useNavigate();

  const submitSearch = (checked?: boolean) => {
    if (activeQuoteOption.value === "quoteNumber") {
      navigate(`${REACT_APP_MANAGER_LINK}/quote-lookup/${quoteSearch}`);
    } else {
      const receivedOrderOnly =
        checked === undefined ? showReceivedOrderOnly : checked;
      setExternalFilterBy([
        { id: activeQuoteOption.value + "_search", value: quoteSearch },
        ...(receivedOrderOnly
          ? [{ id: "status_search", value: "received" }]
          : []),
      ]);
    }
  };

  const handleOnClick: FormEventHandler = (e: SyntheticEvent) => {
    e.preventDefault();
    submitSearch();
  };

  return (
    <div className="p-6">
      <h1 className="pb-4 text-2xl font-semibold text-gray-900">
        Quote Finder
      </h1>
      <form
        onSubmit={handleOnClick}
        className="flex content-center items-center pb-6"
      >
        <Listbox
          key="quote-options-menu"
          defaultValue={QUOTE_OPTIONS[0]}
          value={activeQuoteOption}
          onChange={setActiveQuoteOption}
          by="value"
        >
          {({ open }) => (
            <div className="relative w-full shrink-0 sm:w-48">
              <Listbox.Button className="relative flex w-full items-center gap-x-0.5 border border-gray-300 bg-dm-gray-100 py-2 pl-3 text-left text-sm font-bold leading-none text-dm-brand-blue-800 sm:rounded-l-md">
                <span className="grow">{activeQuoteOption.label}</span>
                <Chevron open={open} />
              </Listbox.Button>

              <Transition as={Fragment} {...TRANSITION_PARAMS}>
                <Listbox.Options className="absolute z-20 -mt-1 flex max-h-60 w-full flex-col gap-y-1 overflow-auto rounded-b-md border border-t-0 border-gray-300 bg-dm-gray-100 py-2 text-sm">
                  {QUOTE_OPTIONS.map((item) => (
                    <Listbox.Option
                      key={`item-${item.value}`}
                      value={item}
                      className={({ active, selected }) =>
                        clsx(
                          active || selected
                            ? "bg-dm-brand-blue-500 text-white"
                            : "text-gray-900",
                          "relative cursor-default select-none py-1 pl-3 pr-9 text-sm"
                        )
                      }
                    >
                      {item.label}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </Transition>
            </div>
          )}
        </Listbox>
        <input
          type="text"
          id="quote-search-field"
          name="quote-search-field"
          placeholder={activeQuoteOption.placeholder}
          value={quoteSearch}
          onChange={(e) => setQuoteSearch(e.target.value)}
          className="z-10 -ml-px block w-80 border-gray-300 py-2 text-sm placeholder-gray-300 focus:border-dm-brand-blue-500 focus:outline-none focus:ring-1 focus:ring-dm-brand-blue-500 max-sm:mt-2 max-sm:w-full"
        />
        <button
          type="submit"
          className="relative items-center space-x-2 border border-l border-gray-300 border-l-transparent bg-gray-50 px-4 py-2 text-center text-sm font-medium text-gray-700 hover:bg-gray-100 focus:z-10 focus:border-dm-brand-blue-500 focus:border-l-dm-brand-blue-500 focus:outline-none focus:ring-1 focus:ring-dm-brand-blue-500 disabled:pointer-events-none disabled:opacity-50 max-sm:mt-2 max-sm:w-full sm:rounded-r-md"
          disabled={!quoteSearch}
        >
          Search
        </button>
      </form>
      {activeQuoteOption.value !== "quoteNumber" && (
        <>
          <div className="mb-4 flex items-center">
            <input
              id="received-order-toggle"
              type="checkbox"
              className="mr-2 h-4 w-4 rounded border-gray-300 text-dm-brand-blue-600 focus:ring-dm-brand-blue-500"
              checked={showReceivedOrderOnly}
              onChange={(e) => {
                setShowReceivedOrderOnly((prev) => !prev);
                if (quoteSearch) {
                  submitSearch(e.target.checked);
                }
              }}
            />
            <label
              htmlFor="received-order-toggle"
              className="text-sm text-gray-700"
            >
              Only show quotes with a received order
            </label>
          </div>
          {columns && (
            <section>
              <ul className="text-sm text-dm-charcoal-500">
                <li>
                  <b>Bold Quotes:</b> These are quotes that have been activated,
                  either active or expired.
                </li>
                <li>
                  <b>Italic Quotes:</b> These are stale quotes, meaning the
                  expiration date has passed and the quote was never activated.
                </li>
              </ul>
            </section>
          )}
        </>
      )}
      <DataTable />
    </div>
  );
};

export default QuoteFinder;
